import React from 'react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type {
  ActionRollup,
  ActionRollupGroup,
} from './hooks/actionRollupHooks';

/**
 * The four things that make a task list need attention, in the order somebody
 * scanning it would want them. Status counts are deliberately not tiles: the
 * table already shows status per row, and repeating it crowds out the numbers
 * that are not otherwise visible.
 */
function attentionTiles(rollup: ActionRollup) {
  return [
    {
      key: 'overdue',
      label: 'Overdue',
      value: rollup.overdue,
      tone: 'tw:text-destructive',
    },
    {
      key: 'due_this_shift',
      label: 'Due this shift',
      value: rollup.due_this_shift,
      tone: 'tw:text-amber-600',
    },
    {
      key: 'blocked',
      label: 'Blocked',
      value: rollup.blocked,
      tone: 'tw:text-muted-foreground',
    },
    {
      key: 'unassigned',
      label: 'Unassigned',
      value: rollup.unassigned,
      tone: 'tw:text-muted-foreground',
    },
  ];
}

function Tile({
  label,
  value,
  tone,
  muted,
}: {
  label: string;
  value: number;
  tone?: string;
  muted?: boolean;
}) {
  return (
    <div className="tw:rounded-md tw:border tw:px-3 tw:py-2">
      <p
        className={cn(
          'tw:text-lg tw:font-semibold tw:tabular-nums tw:leading-none',
          // A zero is good news here, so it should not shout in red.
          value > 0 && !muted ? tone : 'tw:text-muted-foreground'
        )}
      >
        {value}
      </p>
      <p className="tw:mt-1 tw:text-xs tw:text-muted-foreground">{label}</p>
    </div>
  );
}

function GroupRow({ group }: { group: ActionRollupGroup }) {
  const open = group.total - (group.by_status.Closed ?? 0);
  return (
    <li className="tw:flex tw:items-center tw:justify-between tw:gap-2 tw:py-1 tw:text-xs">
      <span className="tw:min-w-0 tw:truncate" title={group.label}>
        {group.label}
      </span>
      <span className="tw:flex tw:shrink-0 tw:items-center tw:gap-1.5">
        {group.overdue > 0 && (
          <Badge variant="destructive" className="tw:px-1.5 tw:py-0">
            {group.overdue} overdue
          </Badge>
        )}
        <span className="tw:tabular-nums tw:text-muted-foreground">
          {open} open / {group.total}
        </span>
      </span>
    </li>
  );
}

type ActionRollupSummaryProps = {
  rollup?: ActionRollup;
  isLoading?: boolean;
  /** Which grouping to break out below the tiles. Omit to show tiles only. */
  groupingKey?: string;
  className?: string;
};

/**
 * Shared by the Actions tab summary bar and the SitStat widget, which want the
 * same numbers at different widths — hence the grid rather than a fixed row.
 */
const ActionRollupSummary: React.FC<ActionRollupSummaryProps> = ({
  rollup,
  isLoading,
  groupingKey,
  className,
}) => {
  if (isLoading && !rollup) {
    return (
      <div
        className={cn('tw:text-xs tw:text-muted-foreground', className)}
        aria-busy="true"
      >
        Loading action summary…
      </div>
    );
  }

  if (!rollup) return null;

  if (rollup.total === 0) {
    return (
      <div className={cn('tw:text-xs tw:text-muted-foreground', className)}>
        No actions yet.
      </div>
    );
  }

  const closed = rollup.by_status.Closed ?? 0;
  const grouping = groupingKey
    ? rollup.groupings.find((candidate) => candidate.key === groupingKey)
    : undefined;

  return (
    <div className={cn('tw:space-y-3', className)}>
      <div className="tw:grid tw:grid-cols-2 tw:gap-2 tw:sm:grid-cols-5">
        <Tile label="Total" value={rollup.total} muted />
        {attentionTiles(rollup).map((tile) => (
          <Tile
            key={tile.key}
            label={tile.label}
            value={tile.value}
            tone={tile.tone}
          />
        ))}
      </div>
      <p className="tw:text-xs tw:text-muted-foreground">
        {closed} of {rollup.total} closed
      </p>
      {grouping && grouping.groups.length > 0 && (
        <div>
          <p className="tw:mb-1 tw:text-xs tw:font-medium tw:text-muted-foreground">
            By {grouping.label.toLowerCase()}
          </p>
          <ul className="tw:divide-y">
            {grouping.groups.map((group) => (
              <GroupRow key={group.key} group={group} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ActionRollupSummary;
